import { cn } from "@/lib/utils";
import React from "react";

interface UnifiedListLayoutProps {
    children: React.ReactNode;
    /**
     * The toolbar containing search, filters, and primary actions.
     * This is placed outside the scrolling content area for persistence.
     */
    toolbar?: React.ReactNode;

    /**
     * Optional empty state component to show when no items exist.
     * This should be handled by the parent, but layout support is provided.
     */
    emptyState?: React.ReactNode;

    /**
     * Layout mode for the content area.
     * 'list' - standard vertical stacking (default)
     * 'grid' - grid layout (responsive columns)
     */
    layoutMode?: "list" | "grid";

    className?: string; // Content area overrides
}

/**
 * A standardized layout for list-based pages (Templates, Thoughts, Entities, etc.).
 * 
 * Design Principles:
 * - "Premium" Aesthetic: Clean, spacious, with a focus on content.
 * - No redundant page titles in the main view (handled by sidebar/nav).
 * - Persistent toolbar area for actions.
 * - Optimized scrolling container.
 */
export function UnifiedListLayout({
    children,
    toolbar,
    layoutMode = "list",
    className
}: UnifiedListLayoutProps) {
    return (
        <div className={cn("flex flex-col flex-1 w-full overflow-hidden", className)}>
            {/* Toolbar Area */}
            {/* Toolbar Area */}
            {toolbar && (
                <div className="flex-shrink-0 border-sidebar-border/30 bg-background backdrop-blur-sm z-10">
                    <div className="w-full max-w-7xl mx-auto px-8 py-8 flex items-center justify-between gap-4">
                        {toolbar}
                    </div>
                </div>
            )}

            {/* Main Scrollable Content */}
            <div className="flex-1 overflow-y-auto min-h-0 scrollbar-hide">
                <div className={cn(
                    "w-full max-w-7xl mx-auto p-8 animate-in fade-in duration-500 slide-in-from-bottom-2",
                    layoutMode === "grid" && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6",
                    className
                )}>
                    {children}
                </div>
            </div>
        </div>
    );
}
